/**
 * LiveStreamingService
 *
 * Domain owner of the community live-streaming Firestore bookkeeping. Bridges
 * the live-streaming use cases (start / end / watchers / liveness) to the
 * Task-1/2 `firestoreService` proxies, serializing every entity the use cases
 * build into opaque `JsonObject` payloads via the Task-3/4 remappers on the way
 * DOWN, and remapping the liveness listener's raw docs back into a typed stats
 * object on the way UP. No Firestore-wire types cross this service's public
 * surface — callers see entities in, a typed stats object / `Unsubscribe` out.
 *
 * Live-chat-message triad (this file's `feat_live_streaming_chat_messages`
 * addition): `fetchLiveMessagesPage` (paginated read), `subscribeToLiveMessages`
 * (realtime `added`/`modified`/`removed` fan-out), and `addLiveMessage` (single
 * write). All three bridge the Task-1 `firestoreService` live-message proxies to
 * typed `CommunityChatMessageEntity` currency via the shared
 * `CommunityChatMessageRemapper` — reads remap raw JSON → entity, the write
 * serializes entity → JSON. This is the web analog of Flutter's
 * `StreamingRepository.getLiveMessages` / `addLiveMessage`
 * (streaming_repository.dart:8-18) wrapping `CommunityDataSource`.
 *
 * Architecture:
 *   UseCase → LiveStreamingService → FirestoreService
 *
 * Flutter sources of truth:
 *   - lib/data/repository/community_repository.dart:15-25
 *     (`addMessage(message, notification)` / `endLiveMessage(...)`)
 *   - lib/data/repository/streaming_repository.dart:24-28
 *     (`setLiveWatchers(liveStreamingMessageId, joined)`)
 *   - lib/presentation/screens/community/live_streaming_screen.dart:704-757
 *     (`subscribeToBroadcastingMessageStats`)
 *
 * Modeled after `VideoCallService` (domain/services/videoCall/videoCallService.ts)
 * — a singleton domain bridge.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { firestoreService } from '@/data/services/firestoreService';
import type { JsonObject } from '@/data/types/json';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { MessageBroadcastingInfoEntity } from '@/domain/entities/info/messageBroadcastingInfoEntity';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';
import { AppNotificationRemapper } from '@/domain/remappers/notification/appNotificationRemapper';
import { CommunityChatMessageRemapper } from '@/domain/remappers/community/communityChatMessageRemapper';
import { MessageBroadcastingInfoRemapper } from '@/domain/remappers/info/messageBroadcastingInfoRemapper';
import type { CommunityMessageChangeHandlers } from '@/domain/services/community/communityService';

/**
 * The typed liveness snapshot the `subscribeToLiveMessage` consumer receives for
 * each raw doc change. The raw change type is surfaced verbatim (incl.
 * `removed`) — the `removed`-skip + audience-vs-broadcaster gating is the
 * consumer's (Task-8 use case / Task-10 hook) concern, not this bridge's
 * (parity `live_streaming_screen.dart:716-727`).
 *
 * All five broadcasting values are exposed verbatim per change (`watchers`,
 * `likes`, `coins`, `isLive`, `latestLiveGiftInfo`). The `watchers → likes →
 * coins` else-if selection and the last-gift overlay trigger (fired only on the
 * `coins` branch with `isLive && latestLiveGiftInfo != null`) are the consumer's
 * (Task-4 hook) concern, matching Flutter, where they live in the SCREEN, not
 * the data hop (parity `live_streaming_screen.dart:729-755`).
 */
export interface LiveStreamStats {
  changeType: RemoteDocumentChangeType;
  broadcasterStreamingId: number;
  isLive: boolean;
  watchers: number;
  likes: number;
  coins: number;
  latestLiveGiftInfo?: LatestLiveGiftInfoEntity;
}

export class LiveStreamingService {
  private static instance: LiveStreamingService;

  private constructor() {}

  static getInstance(): LiveStreamingService {
    if (!this.instance) {
      this.instance = new LiveStreamingService();
    }
    return this.instance;
  }

  /**
   * Start a community live session: serialize the `live` community message +
   * its `MessageBroadcastingInfo` + the `liveStarted` notification, then hand
   * the two opaque payloads to the Task-1 transactional data proxy (which writes
   * both docs atomically under the same owner user doc).
   *
   * Serialization:
   *   - The message is serialized through the shared community remapper
   *     (entity → dto → json). The message entity's OWN `broadcastingInfo` is
   *     left undefined by the start-live build — the message-level broadcasting
   *     struct is attached HERE by overwriting `messageJson.broadcastingInfo`
   *     with the serialized `MessageBroadcastingInfo`
   *     (`message_broadcasting_info.g.dart` always-emit-every-field shape). The
   *     emitted wire shape is identical to the remapper delegation (the entity
   *     field being unset, the remapper emits nothing to overwrite).
   *   - The notification is serialized through the Task-4 entity → dto → json
   *     chain, which routes through the extended `NotificationInfoRemapper.toJson`
   *     so the persisted notification carries `info.broadcastingInfo` + the
   *     8-key `info.usersInfo` (business-parity).
   *
   * `owner = message.messageUsersInfo.owner` — on the community screen the wall
   * owner IS the broadcaster, so the notification is written under the same user
   * doc (Flutter reuses the same `user` DocumentReference for both writes,
   * `community_data_source.dart:135-143`).
   *
   * Flutter: lib/data/data_sources/community_data_source.dart:126-165
   * (`addCommunityMessage`) via `CommunityRepository.addMessage`
   * (community_repository.dart:15-19).
   *
   * @returns Promise<boolean> — true on a successful transactional write.
   */
  async startCommunityLiveStream(args: {
    message: CommunityChatMessageEntity;
    broadcastingInfo: MessageBroadcastingInfoEntity;
    notification: AppNotificationEntity;
  }): Promise<boolean> {
    const { message, broadcastingInfo, notification } = args;

    const messageJson = CommunityChatMessageRemapper.toJson(
      CommunityChatMessageRemapper.toDtoFromEntity(message),
    );
    // Attach the message-level broadcasting struct (the entity's own
    // `broadcastingInfo` is left undefined by the start-live build).
    messageJson.broadcastingInfo = MessageBroadcastingInfoRemapper.toJson(
      MessageBroadcastingInfoRemapper.toDtoFromEntity(broadcastingInfo),
    );

    const notificationJson = AppNotificationRemapper.toJson(
      AppNotificationRemapper.toDtoFromEntity(notification),
    );

    return firestoreService.startCommunityLiveMessage(
      message.messageUsersInfo.owner,
      message.id,
      messageJson,
      notification.id!,
      notificationJson,
    );
  }

  /**
   * End a community live session: merge `isLive: false` onto the live message
   * AND write the `liveEnded` notification, atomically, via the Task-1 data
   * proxy.
   *
   * The message update is the exact nested-map merge Flutter builds at
   * `community_data_source.dart:220-228`:
   * `{ text, updatedTimestamp, broadcastingInfo: { isLive: false } }` — the
   * nested `broadcastingInfo` merge flips ONLY `isLive`, preserving the sibling
   * `watchers` / `broadcasterStreamingId` / `likes` / `coins` on the persisted
   * doc.
   *
   * The notification is serialized through the same Task-4 entity → dto → json
   * chain as `startCommunityLiveStream` (carries `info.broadcastingInfo` + the
   * 8-key `info.usersInfo`). Its owner user doc is
   * `notification.info.usersInfo.userId` (parity `community_data_source.dart:216`).
   *
   * Flutter: lib/data/data_sources/community_data_source.dart:198-245
   * (`endLiveMessage`) via `CommunityRepository.endLiveMessage`
   * (community_repository.dart:21-25).
   *
   * @returns Promise<boolean> — true on a successful transactional write.
   */
  async endCommunityLiveStream(args: {
    messageId: string;
    text: string;
    updatedTimestamp: number;
    notification: AppNotificationEntity;
  }): Promise<boolean> {
    const { messageId, text, updatedTimestamp, notification } = args;

    const messageUpdate: JsonObject = {
      text,
      updatedTimestamp,
      broadcastingInfo: { isLive: false },
    };

    const notificationJson = AppNotificationRemapper.toJson(
      AppNotificationRemapper.toDtoFromEntity(notification),
    );

    return firestoreService.endCommunityLiveMessage(
      messageId,
      messageUpdate,
      notification.info.usersInfo.userId,
      notification.id!,
      notificationJson,
    );
  }

  /**
   * Unload-safe variant of `endCommunityLiveStream` for the broadcaster's
   * page-dispose / tab-kill path. Builds the SAME `messageUpdate` merge payload
   * the transactional path builds
   * (`{ text, updatedTimestamp, broadcastingInfo: { isLive: false } }`, parity
   * `community_data_source.dart:236-244`) and hands it to the lightweight
   * single-`setDoc` data proxy `endCommunityLiveMessageByOwner` — no transaction,
   * no server read, no `collectionGroup` pre-query, so the write can flush during
   * `beforeunload`.
   *
   * WHY (web-specific divergence, finding_5). When the broadcaster kills the
   * browser tab, `beforeunload` fires the end-live write as fire-and-forget. The
   * transactional `endCommunityLiveStream` above is a multi-round-trip
   * read-then-commit preceded by a collection-group lookup; the browser tears the
   * page down before it can land, so the message stays `isLive: true` forever.
   * This path issues the same lightweight single-write shape as the watcher
   * decrement that DOES reliably flush during unload. Flutter (mobile) disposes
   * via the app lifecycle where the transaction completes normally, so this is a
   * web-only reliability path for the tab-kill/unload case; the in-session
   * (SPA-nav) end keeps the transactional `endCommunityLiveStream` above.
   *
   * The broadcaster is (by definition) the wall `owner`, so the live message doc
   * path is known directly (`users/{owner}/community_messages/{messageId}`) and
   * needs no lookup. The `liveEnded` notification (the transactional path's second
   * write) is intentionally NOT written here — on a hard tab-kill the only write
   * that must land is `isLive: false`; the notification is secondary
   * (finding_5 step 2).
   *
   * @param owner - The broadcaster's firebaseId (== wall owner == the message
   *   doc's parent user; verified against the current auth uid in the data layer).
   * @param messageId - The live message `id` (== its Firestore doc id).
   * @param text - The pre-formatted `"{name} was live for {duration}"` end text.
   * @param updatedTimestamp - The end timestamp (shared with `text`'s duration).
   * @returns Promise<boolean> — true on a successful single-write flush.
   */
  async endCommunityLiveStreamByOwner(args: {
    owner: string;
    messageId: string;
    text: string;
    updatedTimestamp: number;
  }): Promise<boolean> {
    const { owner, messageId, text, updatedTimestamp } = args;

    const messageUpdate: JsonObject = {
      text,
      updatedTimestamp,
      broadcastingInfo: { isLive: false },
    };

    return firestoreService.endCommunityLiveMessageByOwner(owner, messageId, messageUpdate);
  }

  /**
   * Increment (join) or decrement (leave) the watcher count on the live message.
   * Thin passthrough to the Task-2 data proxy (parity with Flutter's
   * `StreamingRepository.setLiveWatchers` → `LiveStreamingDataSource.setLiveWatchers`,
   * streaming_repository.dart:24-28 / live_streaming_data_source.dart:32-59).
   *
   * @param liveStreamingMessageId - The live message `id`.
   * @param joined - true increments (+1) on join, false decrements (-1) on leave.
   * @returns Promise<boolean> — true on success.
   */
  async setLiveWatchers(liveStreamingMessageId: string, joined: boolean): Promise<boolean> {
    return firestoreService.setCommunityLiveWatchers(liveStreamingMessageId, joined);
  }

  /**
   * Subscribe to the single live message (the liveness listener) and surface a
   * typed `LiveStreamStats` for EVERY raw doc change. Each change's raw
   * `broadcastingInfo` map is parsed through the Task-3
   * `MessageBroadcastingInfoRemapper` (default-safe on a partial / absent struct,
   * and after Task 1 carrying a typed `latestLiveGiftInfo`) and the fields the
   * consumer needs — `broadcasterStreamingId`, `isLive`, `watchers`, `likes`,
   * `coins`, `latestLiveGiftInfo` — plus the raw `changeType` are exposed. No
   * extra remapper call is needed for the gift struct: `fromDto` already parses
   * the nested `latestLiveGiftInfo`.
   *
   * The change type is surfaced RAW (incl. `removed`): the `removed`-skip and the
   * audience-vs-broadcaster `!isLive` teardown gating are the consumer's concern
   * (Task-8 use case / Task-10 hook), matching Flutter, where
   * `subscribeToBroadcastingMessageStats` skips `removed` (:716-718) and gates
   * `!_isBroadcaster && !isLive` (:723-727) in the SCREEN, not the data hop.
   *
   * Flutter: live_streaming_screen.dart:704-757.
   *
   * @returns An `Unsubscribe`; the caller is responsible for cleanup.
   */
  subscribeToLiveMessage(
    liveStreamingMessageId: string,
    onStats: (stats: LiveStreamStats) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToCommunityLiveMessage(
      liveStreamingMessageId,
      changes => {
        for (const change of changes) {
          const broadcasting = MessageBroadcastingInfoRemapper.fromDto(
            MessageBroadcastingInfoRemapper.fromJson(
              (change.data['broadcastingInfo'] as JsonObject | undefined) ?? {},
            ),
          );
          onStats({
            changeType: change.type,
            broadcasterStreamingId: broadcasting.broadcasterStreamingId,
            isLive: broadcasting.isLive,
            watchers: broadcasting.watchers,
            likes: broadcasting.likes,
            coins: broadcasting.coins,
            latestLiveGiftInfo: broadcasting.latestLiveGiftInfo,
          });
        }
      },
      onError,
    );
  }

  /**
   * Fetch a newest-first page of a live session's chat messages.
   *
   * Proxies `firestoreService.fetchCommunityLiveMessagesPage` (the
   * `live_messages` collection-group filtered by `parentId = liveMessageId`,
   * ordered `createdTimestamp desc`), remaps each raw `JsonObject` to a DTO via
   * `CommunityChatMessageRemapper.fromJson`, then maps DTO → entity — mirroring
   * `CommunityService.fetchMessagesPage` (communityService.ts:85-106).
   *
   * `hasMore` is derived here with strict `=== limit` — parity with Flutter
   * `response.hasMore = querySnapshot.docs.length == limit`
   * (community_data_source.dart:115). The data layer leaves this derivation to
   * the caller (firestoreService.ts:1716-1720).
   *
   * Flutter: streaming_repository.dart:8-13 (`getLiveMessages`) →
   * community_data_source.dart:85-124 (`getCommunityLiveMessages`).
   *
   * @param liveMessageId - The live streaming message id (`parentId` of every
   *   live message in the session).
   * @param lastTimestamp - The previous page's tail `createdTimestamp`, or
   *   `null` for the first page.
   * @param limit - Page size.
   */
  async fetchLiveMessagesPage(
    liveMessageId: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<{
    messages: CommunityChatMessageEntity[];
    lastTimestamp: number | null;
    hasMore: boolean;
  }> {
    const { messages: rawMessages, lastTimestamp: nextLastTimestamp } =
      await firestoreService.fetchCommunityLiveMessagesPage(liveMessageId, lastTimestamp, limit);

    const dtos = rawMessages.map(CommunityChatMessageRemapper.fromJson);
    const messages = dtos.map(CommunityChatMessageRemapper.toEntity);

    return {
      messages,
      lastTimestamp: nextLastTimestamp,
      // Strict `=== limit` (NOT `>=`), parity with community_data_source.dart:115.
      hasMore: dtos.length === limit,
    };
  }

  /**
   * Subscribe to a live session's chat message changes arriving after
   * `sinceTimestamp`, remapping each change's raw doc to a
   * `CommunityChatMessageEntity` and dispatching to the matching callback keyed
   * on `RemoteDocumentChangeType` — mirroring `CommunityService.subscribeToMessages`
   * (communityService.ts:117-146). The per-change `added`/`modified`/`removed`
   * fan-out matches Flutter's `docChanges` routing at
   * live_streaming_screen.dart:635-701; the routing DECISIONS (id-dedupe of the
   * optimistic echo, background unread queueing) stay in the Task-7 hook.
   *
   * The `handlers` bundle is the SHARED `CommunityMessageChangeHandlers` interface
   * (communityService.ts:50-54) so the Task-7 hook can pass the same
   * `useCommunityLiveListener` handler bundle it uses for the wall.
   *
   * @returns An `Unsubscribe` handle the caller is responsible for invoking.
   */
  subscribeToLiveMessages(
    liveMessageId: string,
    sinceTimestamp: number,
    handlers: CommunityMessageChangeHandlers,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToCommunityLiveMessages(
      liveMessageId,
      sinceTimestamp,
      changes => {
        for (const change of changes) {
          const entity = CommunityChatMessageRemapper.toEntity(
            CommunityChatMessageRemapper.fromJson(change.data),
          );
          switch (change.type) {
            case RemoteDocumentChangeType.added:
              handlers.onAdded?.(entity);
              break;
            case RemoteDocumentChangeType.modified:
              handlers.onModified?.(entity);
              break;
            case RemoteDocumentChangeType.removed:
              handlers.onRemoved?.(entity);
              break;
          }
        }
      },
      onError,
    );
  }

  /**
   * Add a single live chat message to its session's nested `live_messages`
   * collection.
   *
   * Bridges entity → DTO → `CommunityChatMessageRemapper.toJson`, then calls
   * `firestoreService.addCommunityLiveMessage(owner, parentId, id, messageData)`
   * — mirroring `CommunityService.addMessage` (communityService.ts:160-168) but
   * targeting the nested live path
   * `users/{owner}/community_messages/{parentId}/live_messages/{id}` instead of
   * the flat wall path. Flutter serializes `message.toJson()`
   * (community_data_source.dart:184).
   *
   * `owner = message.messageUsersInfo.owner` (= the broadcaster's firebaseId, the
   * wall owner) and `parentId = message.parentId` (= the live streaming message
   * id) — both are set by the Task-3 `CommunityChatMessageRemapper.fromLiveInput`
   * build, so a live message ALWAYS carries a non-null `parentId`; it is asserted
   * (`!`) here. (Contrast a wall message, whose `parentId` mirrors
   * `replyToMessage?.id` and may be undefined.)
   *
   * Flutter: streaming_repository.dart:15-17 (`addLiveMessage`) →
   * community_data_source.dart:167-196 (`addCommunityLiveMessage`).
   *
   * @returns `true` on success, `false` on failure (parity with Flutter's
   *   `bool success` pattern, community_data_source.dart:173/189/192).
   */
  async addLiveMessage(message: CommunityChatMessageEntity): Promise<boolean> {
    const dto = CommunityChatMessageRemapper.toDtoFromEntity(message);
    const messageData = CommunityChatMessageRemapper.toJson(dto);
    return firestoreService.addCommunityLiveMessage(
      message.messageUsersInfo.owner,
      // `fromLiveInput` (Task 3) guarantees a non-null `parentId` for a live
      // message (= liveStreamingMessageId); asserted here.
      message.parentId!,
      message.id,
      messageData,
    );
  }
}

// Export singleton instance
export const liveStreamingService = LiveStreamingService.getInstance();
